import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Stethoscope,
  Baby,
  Waves,
  Leaf,
  Star,
  Trophy,
  ChevronRight,
  Play,
  Check,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useGameProgress } from '../hooks';
import {
  generateRescueCase,
  generateOtterCard,
  getRandomRescueFact,
  rescueScenarios,
  rankInfo,
  facilityDescriptions,
} from './data';
import {
  OilCleaningGame,
  FeedingGame,
  SwimmingLessonsGame,
  NetRemovalGame,
  HabitatMatchingGame,
  ReleasePrepGame,
} from './minigames';
import type { RescueCase, MiniGameType, RescueScenarioType } from '../types';

type GameView = 'hub' | 'case-select' | 'case-active' | 'minigame' | 'card-reveal' | 'stats';

const FACILITY_ICONS = {
  medicalBay: Stethoscope,
  nursery: Baby,
  trainingPool: Waves,
  researchLab: Leaf,
};

export function RescueMissionGame() {
  const navigate = useNavigate();
  const { progress, addRescuedOtter, addStars, purchaseFacilityUpgrade } = useGameProgress();

  const [view, setView] = useState<GameView>('hub');
  const [activeCase, setActiveCase] = useState<RescueCase | null>(null);
  const [activeMiniGame, setActiveMiniGame] = useState<MiniGameType | null>(null);
  const [newCard, setNewCard] = useState<ReturnType<typeof generateOtterCard> | null>(null);
  const [caseStartTime, setCaseStartTime] = useState<number>(0);

  const rescueMission = progress.rescueMission;
  const currentRank = rankInfo[rescueMission.rescuerRank];

  // Start a new case
  const startNewCase = useCallback((scenarioType?: RescueScenarioType) => {
    const newCase = generateRescueCase(scenarioType);
    setActiveCase(newCase);
    setCaseStartTime(Date.now());
    setView('case-active');
  }, []);

  // Handle mini-game completion
  const handleMiniGameComplete = useCallback((score: number, stars: number) => {
    if (!activeCase) return;

    addStars(stars);

    // Mark current stage as complete
    const updatedCase = {
      ...activeCase,
      stages: activeCase.stages.map((stage, i) =>
        i === activeCase.currentStage ? { ...stage, completed: true } : stage
      ),
      currentStage: activeCase.currentStage + 1,
    };

    // Check if case is complete
    if (updatedCase.currentStage >= updatedCase.stages.length) {
      updatedCase.completed = true;

      // Generate card
      const recoveryDays = Math.ceil((Date.now() - caseStartTime) / (1000 * 60)) || 1; // Minutes as "days" for demo
      const card = generateOtterCard(updatedCase, recoveryDays);
      setNewCard(card);
      addRescuedOtter(card, stars);
      setView('card-reveal');
    } else {
      setActiveCase(updatedCase);
      setView('case-active');
    }

    setActiveMiniGame(null);
  }, [activeCase, caseStartTime, addStars, addRescuedOtter]);

  // Start a mini-game
  const startMiniGame = useCallback((miniGame: MiniGameType) => {
    setActiveMiniGame(miniGame);
    setView('minigame');
  }, []);

  // Complete a non-minigame stage
  const completeStage = useCallback(() => {
    if (!activeCase) return;

    addStars(1);

    const updatedCase = {
      ...activeCase,
      stages: activeCase.stages.map((stage, i) =>
        i === activeCase.currentStage ? { ...stage, completed: true } : stage
      ),
      currentStage: activeCase.currentStage + 1,
    };

    if (updatedCase.currentStage >= updatedCase.stages.length) {
      updatedCase.completed = true;
      const recoveryDays = Math.ceil((Date.now() - caseStartTime) / (1000 * 60)) || 1;
      const card = generateOtterCard(updatedCase, recoveryDays);
      setNewCard(card);
      addRescuedOtter(card, 1);
      setView('card-reveal');
    } else {
      setActiveCase(updatedCase);
    }
  }, [activeCase, caseStartTime, addStars, addRescuedOtter]);

  // Render current mini-game
  const renderMiniGame = () => {
    if (!activeCase || !activeMiniGame) return null;

    const props = {
      onComplete: handleMiniGameComplete,
      onBack: () => {
        setActiveMiniGame(null);
        setView('case-active');
      },
      speciesId: activeCase.speciesId,
      otterName: activeCase.otterName,
    };

    switch (activeMiniGame) {
      case 'oil-cleaning':
        return <OilCleaningGame {...props} />;
      case 'feeding':
        return <FeedingGame {...props} />;
      case 'swimming-lessons':
        return <SwimmingLessonsGame {...props} />;
      case 'net-removal':
        return <NetRemovalGame {...props} />;
      case 'habitat-matching':
        return <HabitatMatchingGame {...props} />;
      case 'release-prep':
        return <ReleasePrepGame {...props} />;
      default:
        return null;
    }
  };

  // Hub view
  const renderHub = () => (
    <div className="min-h-screen bg-gradient-to-b from-teal-900 to-teal-950 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/games')}
          className="text-white/60 text-sm"
        >
          ← Back
        </button>
        <h1 className="text-xl font-bold text-white">Rescue Center</h1>
        <button
          onClick={() => setView('stats')}
          className="text-white/60"
        >
          <Trophy className="w-5 h-5" />
        </button>
      </div>

      {/* Rank and Stars */}
      <div className="bg-black/30 rounded-2xl p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="text-4xl">{currentRank.badge}</div>
          <div className="flex-1">
            <h2 className="text-white font-bold">{currentRank.name}</h2>
            <p className="text-white/60 text-sm">{currentRank.description}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-yellow-400">
              <Star className="w-4 h-4 fill-yellow-400" />
              <span className="font-bold">{rescueMission.stars}</span>
            </div>
            <p className="text-white/40 text-xs">{rescueMission.totalRescues} rescues</p>
          </div>
        </div>
      </div>

      {/* Start New Rescue */}
      <motion.button
        onClick={() => setView('case-select')}
        className="w-full bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-6 mb-6 text-left"
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">New Rescue</h3>
            <p className="text-white/70 text-sm">
              An otter needs your help!
            </p>
          </div>
          <div className="text-5xl">🦦</div>
        </div>
      </motion.button>

      {/* Facility Upgrades */}
      <h3 className="text-white font-bold mb-3">Your Facility</h3>
      <div className="grid grid-cols-2 gap-3 mb-6">
        {(Object.keys(facilityDescriptions) as Array<keyof typeof facilityDescriptions>).map(key => {
          const facility = facilityDescriptions[key];
          const level = rescueMission.facilityUpgrades[key];
          const Icon = FACILITY_ICONS[key];
          const upgradeCost = (level + 1) * 20;
          const canUpgrade = level < 3 && rescueMission.stars >= upgradeCost;

          return (
            <motion.div
              key={key}
              className="bg-black/30 rounded-xl p-3"
              whileTap={canUpgrade ? { scale: 0.98 } : {}}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-5 h-5 text-teal-400" />
                <span className="text-white font-medium text-sm">{facility.name}</span>
              </div>
              <p className="text-white/60 text-xs mb-2">
                {facility.levels[level]}
              </p>
              <div className="flex items-center gap-1 mb-2">
                {[0, 1, 2, 3].map(i => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full ${
                      i <= level ? 'bg-teal-400' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>
              {level < 3 && (
                <Button
                  size="sm"
                  variant={canUpgrade ? 'primary' : 'secondary'}
                  disabled={!canUpgrade}
                  onClick={() => purchaseFacilityUpgrade(key, upgradeCost)}
                  className="w-full text-xs"
                >
                  <Star className="w-3 h-3" />
                  Upgrade ({upgradeCost})
                </Button>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Recent Rescues */}
      {rescueMission.rescuedOtters.length > 0 && (
        <>
          <h3 className="text-white font-bold mb-3">Recent Rescues</h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {rescueMission.rescuedOtters.slice(-5).reverse().map(otter => (
              <div
                key={otter.id}
                className={`
                  flex-shrink-0 w-24 bg-black/30 rounded-xl p-3 text-center
                  ${otter.rarity === 'legendary' ? 'ring-2 ring-yellow-400' :
                    otter.rarity === 'epic' ? 'ring-2 ring-purple-400' :
                    otter.rarity === 'rare' ? 'ring-2 ring-blue-400' : ''
                  }
                `}
              >
                <div className="text-3xl mb-1">🦦</div>
                <p className="text-white text-xs font-medium truncate">{otter.name}</p>
                <p className="text-white/40 text-xs capitalize">{otter.rarity}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // Case selection view
  const renderCaseSelect = () => (
    <div className="min-h-screen bg-gradient-to-b from-teal-900 to-teal-950 p-4">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => setView('hub')} className="text-white/60 text-sm">
          ← Back
        </button>
        <h1 className="text-xl font-bold text-white">Choose a Rescue</h1>
        <div className="w-12" />
      </div>

      <div className="space-y-3">
        {rescueScenarios.map(scenario => (
          <motion.button
            key={scenario.type}
            onClick={() => startNewCase(scenario.type)}
            className={`
              w-full bg-black/30 rounded-xl p-4 text-left
              ${scenario.rarity === 'legendary' ? 'border-2 border-yellow-500/50' :
                scenario.rarity === 'epic' ? 'border-2 border-purple-500/50' :
                scenario.rarity === 'rare' ? 'border-2 border-blue-500/50' : ''
              }
            `}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-start gap-3">
              <div className="text-3xl">
                {scenario.type === 'oil-spill' ? '🛢️' :
                 scenario.type === 'orphaned-pup' ? '👶' :
                 scenario.type === 'net-entanglement' ? '🥅' :
                 scenario.type === 'pollution-sickness' ? '🏭' :
                 '🏗️'}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-bold">{scenario.title}</h3>
                  <span className={`
                    text-xs px-2 py-0.5 rounded-full capitalize
                    ${scenario.rarity === 'legendary' ? 'bg-yellow-500/30 text-yellow-300' :
                      scenario.rarity === 'epic' ? 'bg-purple-500/30 text-purple-300' :
                      scenario.rarity === 'rare' ? 'bg-blue-500/30 text-blue-300' :
                      scenario.rarity === 'uncommon' ? 'bg-green-500/30 text-green-300' :
                      'bg-white/10 text-white/60'
                    }
                  `}>
                    {scenario.rarity}
                  </span>
                </div>
                <p className="text-white/60 text-sm">{scenario.description}</p>
                <p className="text-white/40 text-xs mt-1">
                  {scenario.stageNames.length} stages
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/40" />
            </div>
          </motion.button>
        ))}

        {/* Random rescue button */}
        <motion.button
          onClick={() => startNewCase()}
          className="w-full bg-gradient-to-r from-purple-600/50 to-pink-600/50 rounded-xl p-4"
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">🎲</span>
            <span className="text-white font-bold">Random Rescue</span>
          </div>
        </motion.button>
      </div>
    </div>
  );

  // Active case view
  const renderActiveCase = () => {
    if (!activeCase) return null;

    const currentStage = activeCase.stages[activeCase.currentStage];
    const scenario = rescueScenarios.find(s => s.type === activeCase.scenarioType);

    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-900 to-teal-950 p-4 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              setActiveCase(null);
              setView('hub');
            }}
            className="text-white/60 text-sm"
          >
            ← Exit Case
          </button>
          <h1 className="text-lg font-bold text-white">{scenario?.title}</h1>
          <div className="w-12" />
        </div>

        {/* Otter info */}
        <div className="bg-black/30 rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="text-5xl">🦦</div>
            <div>
              <h2 className="text-xl font-bold text-white">{activeCase.otterName}</h2>
              <p className="text-white/60 text-sm capitalize">
                {activeCase.speciesId.replace(/-/g, ' ')}
              </p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/60 text-sm">Progress</span>
            <span className="text-white text-sm">
              Stage {activeCase.currentStage + 1}/{activeCase.stages.length}
            </span>
          </div>
          <div className="flex gap-1">
            {activeCase.stages.map((stage, i) => (
              <div
                key={stage.id}
                className={`
                  h-2 flex-1 rounded-full
                  ${stage.completed ? 'bg-green-500' :
                    i === activeCase.currentStage ? 'bg-teal-400' :
                    'bg-white/20'
                  }
                `}
              />
            ))}
          </div>
        </div>

        {/* Current stage */}
        <div className="bg-black/30 rounded-2xl p-4 mb-4">
          <h3 className="text-white font-bold text-lg mb-2">{currentStage.name}</h3>
          <p className="text-white/70 mb-4">{currentStage.description}</p>

          {currentStage.miniGame ? (
            <Button
              onClick={() => startMiniGame(currentStage.miniGame!)}
              className="w-full"
              variant="success"
            >
              <Play className="w-4 h-4" />
              Start Activity
            </Button>
          ) : (
            <Button
              onClick={completeStage}
              className="w-full"
              variant="primary"
            >
              <Check className="w-4 h-4" />
              Complete Stage
            </Button>
          )}
        </div>

        {/* Completed stages */}
        {activeCase.currentStage > 0 && (
          <>
            <h4 className="text-white/60 text-sm mb-2">Completed</h4>
            <div className="space-y-2">
              {activeCase.stages.slice(0, activeCase.currentStage).map(stage => (
                <div
                  key={stage.id}
                  className="bg-green-900/30 rounded-xl p-3 flex items-center gap-3"
                >
                  <Check className="w-5 h-5 text-green-400" />
                  <span className="text-white/80">{stage.name}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Fun fact */}
        <div className="mt-6 bg-teal-800/30 rounded-xl p-4">
          <p className="text-teal-200 text-sm">
            <span className="font-bold">Did you know?</span>{' '}
            {getRandomRescueFact(activeCase.scenarioType)}
          </p>
        </div>
      </div>
    );
  };

  // Card reveal view
  const renderCardReveal = () => {
    if (!newCard) return null;

    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-900 to-teal-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0, rotateY: 180 }}
          animate={{ scale: 1, rotateY: 0 }}
          transition={{ type: 'spring', damping: 15, delay: 0.3 }}
          className={`
            w-64 bg-gradient-to-b from-amber-100 to-amber-200 rounded-2xl p-4 shadow-2xl
            ${newCard.rarity === 'legendary' ? 'ring-4 ring-yellow-400' :
              newCard.rarity === 'epic' ? 'ring-4 ring-purple-400' :
              newCard.rarity === 'rare' ? 'ring-4 ring-blue-400' : ''
            }
          `}
        >
          <div className={`
            text-xs font-bold uppercase tracking-wider mb-2 text-right
            ${newCard.rarity === 'legendary' ? 'text-yellow-600' :
              newCard.rarity === 'epic' ? 'text-purple-600' :
              newCard.rarity === 'rare' ? 'text-blue-600' :
              newCard.rarity === 'uncommon' ? 'text-green-600' :
              'text-gray-500'
            }
          `}>
            {newCard.rarity}
          </div>

          <div className="bg-teal-600 rounded-xl p-6 mb-3">
            <div className="text-6xl text-center">🦦</div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 text-center mb-1">
            {newCard.name}
          </h3>
          <p className="text-gray-500 text-sm text-center capitalize mb-3">
            {newCard.speciesId.replace(/-/g, ' ')}
          </p>

          <div className="border-t border-amber-300 pt-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Rescued from:</span>
              <span className="text-gray-700 capitalize">
                {newCard.scenarioType.replace(/-/g, ' ')}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Recovery:</span>
              <span className="text-gray-700">{newCard.recoveryDays} days</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Status:</span>
              <span className="text-green-600 font-bold">Released!</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-4 right-4"
        >
          <Button
            onClick={() => {
              setNewCard(null);
              setActiveCase(null);
              setView('hub');
            }}
            className="w-full"
            variant="success"
          >
            Continue
          </Button>
        </motion.div>
      </div>
    );
  };

  // Stats view
  const renderStats = () => (
    <div className="min-h-screen bg-gradient-to-b from-teal-900 to-teal-950 p-4">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => setView('hub')} className="text-white/60 text-sm">
          ← Back
        </button>
        <h1 className="text-xl font-bold text-white">Your Stats</h1>
        <div className="w-12" />
      </div>

      {/* Rank progress */}
      <div className="bg-black/30 rounded-2xl p-4 mb-6">
        <h3 className="text-white font-bold mb-3">Rescuer Rank</h3>
        <div className="space-y-2">
          {Object.entries(rankInfo).map(([key, rank]) => {
            const isCurrent = key === rescueMission.rescuerRank;
            const isUnlocked = rescueMission.totalRescues >= rank.minRescues;

            return (
              <div
                key={key}
                className={`
                  flex items-center gap-3 p-2 rounded-lg
                  ${isCurrent ? 'bg-teal-600/30' : ''}
                `}
              >
                <span className="text-2xl">{isUnlocked ? rank.badge : '🔒'}</span>
                <div className="flex-1">
                  <p className={`font-medium ${isUnlocked ? 'text-white' : 'text-white/40'}`}>
                    {rank.name}
                  </p>
                  <p className="text-white/40 text-xs">{rank.minRescues}+ rescues</p>
                </div>
                {isCurrent && (
                  <span className="text-teal-400 text-xs">Current</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Collection */}
      <div className="bg-black/30 rounded-2xl p-4">
        <h3 className="text-white font-bold mb-3">
          Collection ({rescueMission.rescuedOtters.length})
        </h3>
        <div className="grid grid-cols-4 gap-2">
          {rescueMission.rescuedOtters.map(otter => (
            <div
              key={otter.id}
              className={`
                aspect-square bg-black/30 rounded-lg flex items-center justify-center text-2xl
                ${otter.rarity === 'legendary' ? 'ring-2 ring-yellow-400' :
                  otter.rarity === 'epic' ? 'ring-2 ring-purple-400' :
                  otter.rarity === 'rare' ? 'ring-2 ring-blue-400' : ''
                }
              `}
              title={otter.name}
            >
              🦦
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Main render
  if (view === 'minigame') {
    return renderMiniGame();
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={view}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
      >
        {view === 'hub' && renderHub()}
        {view === 'case-select' && renderCaseSelect()}
        {view === 'case-active' && renderActiveCase()}
        {view === 'card-reveal' && renderCardReveal()}
        {view === 'stats' && renderStats()}
      </motion.div>
    </AnimatePresence>
  );
}
